<template>
  <Teleport to="body">
    <div
      v-if="show"
      class="fixed inset-0 z-9999 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
      @click="handleBackdropClick"
    >
      <div
        class="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
        @click.stop
      >
        <!-- Contenu selon l'étape -->
        <div class="p-6 md:p-8">
          <!-- Étape 1 -->
          <div v-if="currentStep === 1">
            <!-- Avertissement -->
            <div class="bg-orange-50 border-l-4 border-orange-400 p-4 md:p-5 mb-6">
              <div class="flex items-start gap-3">
                <div class="shrink-0 mt-0.5">
                 <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 16 16"><g fill="none"><path fill="url(#SVG46elwcsk)" d="M2 8a6 6 0 1 1 12 0A6 6 0 0 1 2 8"/><path fill="url(#SVGQMDY1c6m)" d="M8 10a.75.75 0 1 0 0 1.5a.75.75 0 0 0 0-1.5m0-5.5a.5.5 0 0 0-.492.41L7.5 5v3.5l.008.09a.5.5 0 0 0 .984 0L8.5 8.5V5l-.008-.09A.5.5 0 0 0 8 4.5"/><defs><linearGradient id="SVG46elwcsk" x1="3.875" x2="11.75" y1=".125" y2="15.125" gradientUnits="userSpaceOnUse"><stop stop-color="#ffcd0f"/><stop offset="1" stop-color="#fe8401"/></linearGradient><linearGradient id="SVGQMDY1c6m" x1="6" x2="9.213" y1="4.5" y2="11.844" gradientUnits="userSpaceOnUse"><stop stop-color="#4a4a4a"/><stop offset="1" stop-color="#212121"/></linearGradient></defs></g></svg>
                </div>
                <div class="flex-1">
                  <h3 class="text-gray-900 font-bold text-base mb-2">Avertissement Important</h3>
                  <p class="text-gray-700 text-sm leading-relaxed mb-2.5">
                    Déposer un signalement n'est pas une démarche neutre. Cela peut avoir des implications sérieuses pour les personnes visées.
                  </p>
                  <p class="text-gray-700 text-sm leading-relaxed">
                    Il est important de bien considérer la véracité des faits décrits et des propos rapportés afin d'éviter toute calomnie ou diffamation. Pour votre information, la recevabilité du signalement dépend du sérieux et de la validité de la dénonciation.
                  </p>
                </div>
              </div>
            </div>

            <!-- Questions-Réponses Étape 1 -->
            <div class="space-y-6">
              <div>
                <h3 class="text-[#0d4a6f] font-bold text-sm md:text-base mb-2.5">
                  Que se passe-t-il après le dépôt d'un signalement sur la plateforme ?
                </h3>
                <p class="text-gray-700 text-sm leading-relaxed">
                  Après le dépôt de votre signalement, la plateforme génère un code confidentiel unique dont vous seul avez connaissance. Vous pouvez demander, à la fin de la procédure de dépôt de votre signalement, à le recevoir par courriel électronique, y compris si vous avez souhaité rester anonyme. Ce code vous permettra, le cas échéant, d'accéder depuis la page d'accueil de cette plateforme à votre signalement en cliquant sur le bouton « Accéder à un signalement existant », pour le compléter ou répondre, via une messagerie sécurisée, à des demandes de précisions ou de pièces complémentaires que nous vous adresserions. Ce code confidentiel est unique, crypté et inconnu des services de la Cour. En cas de perte ou d'oubli, il ne pourra donc pas vous être de nouveau communiqué.
                </p>
              </div>

              <div>
                <h3 class="text-[#0d4a6f] font-bold text-sm md:text-base mb-2.5">
                  Puis-je déposer un signalement de façon anonyme ?
                </h3>
                <p class="text-gray-700 text-sm leading-relaxed">
                  Oui, vous pouvez choisir de déposer votre signalement de manière anonyme. Votre signalement et l'ensemble des échanges auquel il pourrait donner lieu seront intégralement chiffrés.
                </p>
              </div>
            </div>
          </div>

          <!-- Étape 2 -->
          <div v-if="currentStep === 2">
            <div class="space-y-6">
              <div>
                <h3 class="text-[#0d4a6f] font-bold text-sm md:text-base mb-2.5">
                  Est-ce que mon signalement reste confidentiel ?
                </h3>
                <p class="text-gray-700 text-sm leading-relaxed mb-2.5">
                  Oui votre signalement reste confidentiel.
                </p>
                <p class="text-gray-700 text-sm leading-relaxed mb-2.5">
                  Les personnes habilitées à traiter les signalements prennent toutes les mesures utiles pour préserver la sécurité et garantir la confidentialité des documents et informations, que ce soit à la collecte, le traitement ou la conservation.
                </p>
                <p class="text-gray-700 text-sm leading-relaxed">
                  Toutefois, votre signalement est susceptible d'être utilisé par la Cour des Comptes dans le cadre de ses travaux ou encore d'être adressé à l'autorité judiciaire ou à toute autorité administrative qu'il est susceptible d'intéresser.
                </p>
              </div>

              <div>
                <h3 class="text-[#0d4a6f] font-bold text-sm md:text-base mb-2.5">
                  Comment la recevabilité de mon signalement est-elle appréciée ?
                </h3>
                <p class="text-gray-700 text-sm leading-relaxed mb-2.5">
                  La recevabilité de votre signalement sera notamment évaluée au regard des critères suivants :
                </p>
                <ul class="space-y-2 text-gray-700 text-sm">
                  <li class="flex items-start">
                    <span class="text-green-600 mr-2 mt-0.5">✓</span>
                    <span>les faits rapportés relèvent-ils du champ de compétence de la juridiction financière ?</span>
                  </li>
                  <li class="flex items-start">
                    <span class="text-green-600 mr-2 mt-0.5">✓</span>
                    <span>les faits rapportés sont-ils décrits de façon suffisamment précise ?</span>
                  </li>
                  <li class="flex items-start">
                    <span class="text-green-600 mr-2 mt-0.5">✓</span>
                    <span>le signalement est-il fait de bonne foi ?</span>
                  </li>
                </ul>
              </div>

              <div>
                <h3 class="text-[#0d4a6f] font-bold text-sm md:text-base mb-2.5">
                  Accessibilité
                </h3>
                <p class="text-gray-700 text-sm leading-relaxed">
                  La présente plateforme, conforme aux directives d'accessibilité de niveau AA définies par la norme WAI (WCAG 2.0) est accessible exclusivement aux Organisations de la Société Civile (OSC).
                </p>
              </div>
            </div>
          </div>

          <!-- Étape 3 -->
          <div v-if="currentStep === 3">
            <h2 class="text-[#0d4a6f] font-bold text-xl md:text-2xl mb-5">
              Traitement des données à caractère personnel
            </h2>
            
            <div class="space-y-4 text-gray-700 text-sm leading-relaxed">
              <p>
                La Cour des comptes traite les données recueillies dans ce formulaire pour l'analyse et le suivi de votre signalement. Ce traitement est fondé sur la mission d'intérêt public qui lui est confiée de s'assurer du bon emploi des ressources et biens publics.
              </p>
              <p>
                Les données sont transmises au comité habilité à cet effet au sein du Parquet général près la Cour des comptes et sont conservées pendant 5 ans à compter de leur collecte.
              </p>
              <p>
                Conformément aux dispositions légales et réglementaires applicables, vous bénéficiez d'un droit d'accès, de rectification et d'effacement de vos données personnelles, ou encore de limitation du traitement. Vous pouvez également, pour des motifs légitimes, vous opposer au traitement des données vous concernant.
              </p>
              <p>
                Toutefois, la personne concernée par un signalement ne peut pas s'opposer par principe au traitement de ses données personnelles. Elle pourra néanmoins s'opposer au traitement de ses données personnelles en cas d'erreur et en prouvant que le traitement de ses données n'est plus justifiés. La personne qui fait l'objet d'un signalement ne peut en aucun cas obtenir d'informations concernant l'identité de l'émetteur de l'alerte sur le fondement de son droit d'accès. De manière générale, lorsque l'exercice de vos droits d'accès, de rectification ou d'effacement, les personnes exercent leurs droits d'accès, ils ne peuvent pas via l'exercice de ce droit, obtenir communication de données relatives à des tiers. L'ensemble de ces droits, sous réserve de la production d'un justificatif d'identité, peuvent être exercés en envoyant un message via la messagerie sécurisée de la plateforme accessible avec votre code confidentiel ou en écrivant à ou référent à l'adresse............. Enfin, si vous estimez, après nous avoir contactés, que vos droits « Informatique et Libertés » ne sont pas respectés, vous pouvez adresser une réclamation à l'APDP.
              </p>
            </div>
          </div>
        </div>

        <!-- Navigation -->
        <div class=" px-4 md:px-8 py-4 md:py-5 flex flex-col sm:flex-row items-center justify-between gap-3 ">
          <button
            v-if="currentStep > 1"
            @click="previousStep"
            class="w-full sm:w-auto flex items-center justify-center gap-2 px-5 py-2.5 bg-[#0d4a6f] text-white text-sm font-semibold rounded hover:bg-[#0a3a56] transition-all"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-circle-arrow-left-icon lucide-circle-arrow-left"><circle cx="12" cy="12" r="10"/><path d="m12 8-4 4 4 4"/><path d="M16 12H8"/></svg>
            PRÉCÉDENT
          </button>
          <div v-else class="hidden sm:block"></div>

          <div v-if="currentStep < 3" class="w-full sm:w-auto flex justify-end">
            <button
              @click="nextStep"
              class="w-full sm:w-auto flex items-center justify-center gap-2 px-5 py-2.5 bg-[#0d4a6f] text-white text-sm font-semibold rounded hover:bg-[#0a3a56] transition-all"
            >
              SUIVANT
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-circle-arrow-right-icon lucide-circle-arrow-right"><circle cx="12" cy="12" r="10"/><path d="m12 16 4-4-4-4"/><path d="M8 12h8"/></svg>
            
            </button>
          </div>
          
          <div v-else class="w-full sm:w-auto flex flex-col sm:flex-row gap-3">
            <button
              @click="handleReject"
              class="w-full sm:w-auto px-5 py-2.5 bg-red-600 text-white text-sm font-semibold rounded hover:bg-red-700 transition-all"
            >
              FERMER
            </button>
            <button
              @click="handleAccept"
              class="w-full sm:w-auto px-5 py-2.5 bg-green-600 text-white text-sm font-semibold rounded hover:bg-green-700 transition-all"
            >
              ACCEPTER
            </button>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted, watch } from 'vue'
interface Props {
  show?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  show: true
})

const emit = defineEmits<{
  close: []
  accept: []
  reject: []
}>()

const currentStep = ref(1)

const nextStep = () => {
  if (currentStep.value < 3) {
    currentStep.value++
  }
}

const previousStep = () => {
  if (currentStep.value > 1) {
    currentStep.value--
  }
}

const handleAccept = () => {
  console.log('Conditions acceptées')
  emit('accept')
  emit('close')
}

const handleReject = () => {
  console.log('Conditions refusées')
  emit('reject')
  emit('close')
}

const handleBackdropClick = (event: MouseEvent) => {
  // Ne pas fermer si on clique sur le backdrop
}

// Désactiver le scroll du body pendant que le modal est ouvert
watch(() => props.show, (isShowing) => {
  if (isShowing) {
    document.body.style.overflow = 'hidden'
  } else {
    document.body.style.overflow = 'auto'
  }
}, { immediate: true })

onMounted(() => {
  if (props.show) {
    document.body.style.overflow = 'hidden'
  }
})

onUnmounted(() => {
  document.body.style.overflow = 'auto'
})
</script>